/**
 * E2E renderer test probe — lets Playwright drive the SHARED onboarding
 * lifecycle tracker through the real `track()` pipeline via
 * `globalThis.__irisOnboardingTelemetry`.
 *
 * WHY: the wizard's canonical events must be receipted at PostHog's send
 * boundary, not just at the tracker's unit boundary — a builder unit test still
 * passes if enrichment, validation or the provider fan-out drops the event on
 * the way out. The wizard's screens are not reachable in this lane (the
 * first-launch funnel is gated on real bridge state), so the probe calls the
 * same tracker the wizard calls, with the same `track()` behind it: enrichment,
 * event-name validation and PostHog capture all run for real.
 *
 * Mirrors the existing renderer probes (device-probe.ts, calibration-probe.ts):
 * same IRIS_E2E_TEST gate, same single global assignment, no product behaviour.
 * Per `stay-in-scope` (ai.knowledge/global) the surface is limited to the
 * step, failure and terminal emissions asserted by the receipt.
 */
import { EVENT_CATEGORIES } from '#~/analytics/constants/index.js';
import { createOnboardingLifecycleTracker } from '#~/analytics/domains/onboarding-lifecycle.js';
import type { AnalyticsTrackFn } from '#~/analytics/domains/types.js';
import type { OnboardingStage, OnboardingStep } from '#~/analytics/constants/index.js';
import type { EventCategory } from '#~/analytics/constants/event-categories.js';
import type { EventProperties } from '#~/analytics/types/properties.js';
import { track as trackAnalyticsEvent } from '#~/analytics/track.js';

interface OnboardingTelemetryProbe {
  stepViewed(stage: OnboardingStage, step: OnboardingStep): void;
  succeeded(stage: OnboardingStage, step: OnboardingStep): void;
  skipped(stage: OnboardingStage, step: OnboardingStep): void;
  failed(stage: OnboardingStage, step: OnboardingStep, rawError: string, terminal: boolean): void;
}

/**
 * Renderer-process gate. `process.env` is NOT populated in this renderer even
 * with nodeIntegration — the preload bridge is the only place the inherited OS
 * environment is visible (see calibration-probe.ts for the full note; a bare
 * `process.env.IRIS_E2E_TEST` read silently never activates in the packaged app).
 */
const isE2eEnabled = (): boolean => {
  try {
    return (
      (globalThis as { window?: { electron?: { process?: { env?: Record<string, string> } } } })
        .window?.electron?.process?.env?.IRIS_E2E_TEST === '1'
    );
  } catch {
    return false;
  }
};

/**
 * Forward a probe emit into real `track()`. Do not pre-filter the name:
 * a superseded `onboarding_*` string must reach `validateEvent` so the
 * send-boundary receipt can go red if that name leaks. The category
 * guard stays so this helper cannot mint a non-onboarding event.
 * Per rule: stay-in-scope (ai.knowledge/global).
 */
export function sendOnboardingProbeEvent(
  name: string,
  category: EventCategory,
  properties?: EventProperties
): void {
  if (category !== EVENT_CATEGORIES.ONBOARDING) return;
  void trackAnalyticsEvent({
    name,
    category,
    ...(properties !== undefined ? { properties } : {}),
    timestamp: Date.now()
  });
}

const trackOnboarding: AnalyticsTrackFn = (name, category, properties) => {
  sendOnboardingProbeEvent(name, category, properties);
};

if (isE2eEnabled()) {
  const tracker = createOnboardingLifecycleTracker(trackOnboarding);
  const probe: OnboardingTelemetryProbe = {
    stepViewed(stage: OnboardingStage, step: OnboardingStep): void {
      tracker.trackStepViewed({ stage, step });
    },

    succeeded(stage: OnboardingStage, step: OnboardingStep): void {
      tracker.trackSucceeded({ stage, step }, { camera_added: true });
    },

    skipped(stage: OnboardingStage, step: OnboardingStep): void {
      tracker.trackSkipped({ stage, step }, { camera_added: false, reason: 'explore_app' });
    },

    failed(
      stage: OnboardingStage,
      step: OnboardingStep,
      rawError: string,
      terminal: boolean
    ): void {
      tracker.trackFailed({ context: { stage, step }, rawError, terminal });
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (globalThis as any).__irisOnboardingTelemetry = probe;
}
